import { FC, useState } from 'react';
import { Box, Button, Tag, TextButton, Text } from '@wix/design-system';
import _ from 'lodash';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { useShallowRouter } from '@components/Hooks/useRouter';
import { updateQueryParams } from '@utils/route';
import { FilterTagProps, useFilterTags } from './useFilterTags';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import SearchTermTag from './SearchTermTag';
import s from './FilterTags.module.scss';

const MAX_TAGS = 5;

interface FilterTagsProps {
  hideSearchTermTag?: boolean;
}

const FilterTags = ({ hideSearchTermTag }: FilterTagsProps) => {
  const {
    state: {
      filters: { search },
    },
  } = useTemplatesContext();
  const { tags } = useFilterTags();
  const containerClasses = `${s.container} ${s.padding36}`;

  if ((!search || hideSearchTermTag) && _.isEmpty(tags)) {
    return null;
  }

  return (
    <Box className={containerClasses} direction="vertical">
      {search && <SearchTermTag size="large" />}
      <ListedFilterTags tags={tags} />
    </Box>
  );
};

const ListedFilterTags = ({ tags }: { tags: FilterTagProps[] }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  const moreCount = _.size(tags) - MAX_TAGS;
  const hasShowMoreButton = _.size(tags) > MAX_TAGS;
  const tagsToDisplay = showAll ? tags : tags.slice(0, MAX_TAGS);
  const { logBiClick } = useTemplatesBi({ is_mobile: false });

  if (_.size(tags) === 0) {
    return <></>;
  }

  return (
    <Box className={s.filterTagsContainer}>
      <FilteredByLabel />
      {tagsToDisplay.map(({ key, onRemove }) => {
        return (
          <Tag
            size="medium"
            key={key}
            id="standard"
            theme="lightOutlined"
            onRemove={() => onRemove?.()}
          >
            {t(key)}
          </Tag>
        );
      })}
      {hasShowMoreButton && (
        <ShowMoreButton
          moreCount={moreCount}
          onClick={() => {
            logBiClick({
              click_type: `show ${showAll ? 'less' : 'more'} filters`,
            });
            setShowAll((bool) => !bool);
          }}
          showMoreText={!showAll}
        />
      )}
      <ClearAllFiltersButton />
    </Box>
  );
};

const FilteredByLabel = () => {
  const { t } = useTranslation();
  return (
    <Box>
      <Text size="small" weight="thin" secondary>
        {t('marketplace.templates-page.filter-tag-label')}
      </Text>
    </Box>
  );
};

interface IClearAllProps {}
const ClearAllFiltersButton: FC<IClearAllProps> = () => {
  const { t } = useTranslation();
  const { navigate, router } = useShallowRouter();
  const { logBiClick } = useTemplatesBi({ is_mobile: false });

  const clearAll = () => {
    logBiClick({
      click_type: 'Clear all filters',
    });
    navigate(updateQueryParams({}, router, {}, { search: true }), false);
  };

  const text = t('marketplace.templates-page.filter-tag.clear-filters');

  return (
    <TextButton
      dataHook="tag-clear-all"
      underline="always"
      skin="dark"
      size="small"
      onClick={clearAll}
    >
      {text}
    </TextButton>
  );
};

interface IShowMoreProps {
  onClick: () => void;
  moreCount: number;
  showMoreText?: boolean;
}

const ShowMoreButton: FC<IShowMoreProps> = ({
  onClick,
  showMoreText,
  moreCount,
}) => {
  const { t } = useTranslation();

  const text = showMoreText
    ? t('fliters-more-count', {
        moreCount,
      })
    : t('fliters-show-less');

  return (
    <Button
      size="small"
      dataHook="tag-more-count"
      removable={false}
      onClick={onClick}
      priority="secondary"
    >
      {text}
    </Button>
  );
};

export default FilterTags;
